import { Dispatch, FC, Fragment, useEffect, useState } from "react";
import {
    Box, Chip, FormControl, InputLabel,
    MenuItem, Select, TextField, Grid, Typography
} from "@mui/material";
import { useStyles } from './styles';
import { LoadingButton } from "@mui/lab";
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import { ActionType, AlertType } from "../../types/AlertType";
import { http } from "../../utils/Http";
import { FeatureType } from "../content/parametrage/user/Tabs/profil/Profil";
import { DataProfil } from "../../types/DataTypes";
import Cookies from "universal-cookie";
import axios, { AxiosInstance } from "axios";
import CheckAttr from "../../utils/CheckAttr";
import { convertBase64 } from "../../utils/utils";

interface ProfilToCreate {
    id?: number;
    libelle: string;
    featureIds?: number[];
    manual_user?: {
        filename: string;
        encoded_file: string;
    };
}

interface InputValidation {
    libelle: boolean;
    feature: boolean;
}

interface ProfilFormProps {
    onClose: () => void;
    dataFeatures: FeatureType[];
    profil?: DataProfil;
    setOpenSnack: Dispatch<React.SetStateAction<boolean>>;
    setAlerteMessage: Dispatch<React.SetStateAction<string>>;
    setAlertType: Dispatch<React.SetStateAction<AlertType>>;
    handleRefreshProfilsDatas: (profil: DataProfil, action: ActionType) => void;
}

const resetProfilToCreate: ProfilToCreate = {
    libelle: '',
    featureIds: [],
    // profil_user_id: undefined
}

const resetInpuValidation: InputValidation = {
    libelle: false,
    feature: false
}

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const ProfilForm: FC<ProfilFormProps> = ({
    dataFeatures,
    profil,
    onClose,
    setOpenSnack,
    setAlerteMessage,
    setAlertType,
    handleRefreshProfilsDatas
}) => {
    const classes = useStyles();
    const [isClickSave, setIsClickSave] = useState<boolean>(false);
    const [openSelectMultiple, setOpenSelectMultiple] = useState<boolean>(false);

    const [profilToSave, setProfilToSave] = useState<ProfilToCreate>(resetProfilToCreate);
    const [errorInput, setErrorInput] = useState<InputValidation>(resetInpuValidation);

    const cookies = new Cookies();
    const token: string = cookies.get("jwt");

    const http: AxiosInstance = axios.create({
        baseURL: urlBaseApi,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });

    useEffect(() => {
        if (profil?.id) {
            const tabFeatures: number[] = [];
            for (let feature of profil?.features) {
                tabFeatures.push(feature?.id);
            }

            const profilToUpdate: ProfilToCreate = {
                id: profil?.id,
                libelle: profil?.libelle,
                featureIds: tabFeatures
            };

            setProfilToSave(profilToUpdate);
        }
    }, []);

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        setProfilToSave({
            ...profilToSave,
            [name]: value
        })
    };

    const isValidate = (): boolean => {
        // Reinitialise les variables des validations
        setErrorInput(resetInpuValidation);

        if (profilToSave?.libelle.length === 0 || !profilToSave?.featureIds) {
            if (profilToSave?.libelle?.length === 0) {
                setErrorInput((prev: InputValidation) => ({ ...prev, libelle: true }));
            }

            if (!profilToSave?.featureIds) {
                setErrorInput((prev: InputValidation) => ({ ...prev, feature: true }));
            }

            setAlerteMessage("Merci de remplir les champs en rouge !");
            setAlertType("warning");
            setOpenSnack(true);

            return false;
        }
        return true;
    };

    const handleCloseSelectMulti = () => {
        setOpenSelectMultiple((prev) => !prev);
    };

    const handleChangeSelectMultitple = (e: any) => {
        e.stopPropagation();
        handleCloseSelectMulti();
        setProfilToSave((prev) => ({
            ...prev,
            featureIds: e.target.value,
        }));
    };

    // File converti to BASE64..........
    const handleChangeFile = async (event: any) => {
        const file = event.target.files[0];
        const base64Front = await convertBase64(file);

        setProfilToSave((prev) => ({
            ...prev,
            manual_user: {
                filename: file.name as string,
                encoded_file: base64Front as string,
            }
        }));
    };

    const handleSave = () => {
        if (isValidate()) {
            setIsClickSave(true);

            if (profil?.id) {
                http.put(`profil/edit/${profilToSave?.id}`, profilToSave)
                    .then(res => {
                        let features = dataFeatures.filter(feature => profilToSave?.featureIds?.includes(feature?.id));

                        const profil: DataProfil = {
                            id: res?.data?.profil?.id,
                            libelle: res?.data?.profil?.libelle,
                            manual_user: res?.data?.profil?.manual_user,
                            features: features,
                        }

                        handleRefreshProfilsDatas(profil, "UPDATE");
                        setAlerteMessage("Enregistrement efféctuée !");
                        setAlertType("success");
                        setOpenSnack(true);
                        onClose();
                    }).catch(error => {
                        console.log(error);
                        setAlertType("error");
                        setOpenSnack(true);
                    }).finally(() => {
                        setIsClickSave(false);
                    });
            } else {
                http.post('profil/create', profilToSave)
                    .then(res => {
                        const features = dataFeatures.filter(feature => profilToSave?.featureIds?.includes(feature?.id));

                        const profil: DataProfil = {
                            id: res?.data?.profil?.id,
                            libelle: res?.data?.profil?.libelle,
                            manual_user: res?.data?.profil?.manual_user,
                            features: features,
                        }

                        handleRefreshProfilsDatas(profil, "CREATE");
                        setAlerteMessage("Enregistrement efféctuée !");
                        setAlertType("success");
                        setOpenSnack(true);
                        onClose();
                    }).catch(error => {
                        console.log(error);
                        setAlertType("error");
                        setOpenSnack(true);
                    }).finally(() => {
                        setIsClickSave(false);
                    });
            }
        }
    };

    return (
        <Fragment>
            <Box className={classes.root}>
                <Grid container spacing={2} columns={{ xs: 6, sm: 6, md: 6 }}>
                    <Grid item xs={6} sm={6} md={6}>
                        <TextField
                            name="libelle"
                            label="Libelle"
                            color="warning"
                            autoComplete="off"
                            disabled={CheckAttr(profil)}
                            value={profilToSave?.libelle}
                            error={errorInput?.libelle}
                            onChange={handleChange}
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={6} sm={6} md={6}>
                        <FormControl variant="outlined" className={classes.formControl}>
                            <InputLabel htmlFor="profil-contact" color="warning">
                                Fonctionnalité
                            </InputLabel>
                            <Select
                                open={openSelectMultiple}
                                onClick={() => handleCloseSelectMulti()}
                                multiple
                                error={errorInput.feature}
                                displayEmpty
                                value={profilToSave?.featureIds || []}
                                onChange={handleChangeSelectMultitple}
                                renderValue={(selected) => {
                                    return (
                                        <div className={classes.chips}>
                                            {((selected as number[]) || []).map((value) => (
                                                <Chip
                                                    key={value}
                                                    label={dataFeatures.find(item => item.id === value)?.libelle}
                                                    className={classes.chip}
                                                />
                                            ))}
                                        </div>
                                    );
                                }}
                                label="Fonctionnalité"
                                color="warning"
                                inputProps={{
                                    name: 'profilContact',
                                    id: 'profil-contact',
                                }}
                                MenuProps={{
                                    sx: {
                                        "&& .Mui-selected": {
                                            backgroundColor: "#080d24",
                                            color: "#fff"
                                        }
                                    }
                                }}
                            >
                                <MenuItem disabled value="" dense></MenuItem>
                                {dataFeatures.map(feature => (
                                    <MenuItem
                                        key={feature.id}
                                        value={feature.id}
                                        onClick={() => handleCloseSelectMulti()}
                                        dense
                                    >
                                        {feature.libelle}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={6} md={6}>
                        <div>
                            <label htmlFor="file">Manuel d'utilisateur :</label> <br />
                            <Typography display="initial">
                                <input
                                    accept="*"
                                    className={classes.formControl}
                                    id="raised-button-file"
                                    name="piecesJointes"
                                    onChange={handleChangeFile}
                                    multiple
                                    type="file"
                                />
                            </Typography>
                        </div>
                    </Grid>
                </Grid>
                <Box display="flex" justifyContent="center">
                    <LoadingButton
                        onClick={() => handleSave()}
                        endIcon={profil?.id ? <SaveIcon /> : <AddIcon />}
                        loading={isClickSave}
                        loadingPosition="end"
                        sx={{ textTransform: "none" }}
                        variant="contained"
                        color="warning"
                    >
                        {profil?.id ? "Enregistrer" : "Ajouter"}
                    </LoadingButton>
                </Box>
            </Box >
        </Fragment>
    );
};

export default ProfilForm;